// ClientErrorReporter — when debug mode is on, catches uncaught JS errors,
// unhandled promise rejections, and (when the admin toggle is on)
// `console.error` calls. Asks the user to confirm filing as a GitHub issue,
// captures a screenshot, and posts to /api/bug-report. The server fingerprints
// the stack and either comments on an existing open issue with the same
// fingerprint or creates a new one.
//
// Mounted once at the app level alongside ServerErrorWatcher. No-op when
// debug mode is off.

import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { getDebugModeEnabled } from './debugMode';
import { DASHBOARD_PATHS } from "@/lib/paths";

const STORAGE_KEY = 'bp_debug_mode';
// Per-tab dedup: never prompt twice for the same (message, top-frame) pair.
// Cleared on reload — debug sessions are short.
const promptedKeys = new Set<string>();

interface CapturedError {
  errorType: string;
  message: string;
  stack: string;
}

function topFrameKey(stack: string): string {
  const firstLine = stack.split('\n').find((l) => /\bat\s+/.test(l)) ?? '';
  return firstLine.trim().slice(0, 200);
}

async function captureScreenshot(): Promise<Blob | null> {
  try {
    const htmlToImage = await import('html-to-image');
    // Fast path. Three things were dominating capture time in the old code:
    //   1. `pixelRatio: dpr` rendered at 2-3× (retina) so the canvas was
    //      4-9× larger than the viewport. Pixel write + encode cost both
    //      scale with the canvas area. `pixelRatio: 1` is fine for triage.
    //   2. `cacheBust: true` appended a query string to every <img> URL,
    //      forcing the browser to re-fetch images already in cache.
    //   3. Font embedding — html-to-image's default path inlines every
    //      @font-face as base64 so the rendered image looks identical to
    //      the live page. Doesn't matter for a triage screenshot; cuts
    //      hundreds of ms on a dashboard with many web fonts.
    // `toBlob` itself replaces the old `toPng` → manual base64-decode →
    // Blob round-trip with a native canvas.toBlob call. The server stores
    // the upload as-is and is free to transcode before any downstream
    // embed (e.g. into a GitHub issue body).
    return await htmlToImage.toBlob(document.body, {
      pixelRatio: 1,
      cacheBust: false,
      skipFonts: true,
    });
  } catch (err) {
    console.warn('ClientErrorReporter: screenshot failed', err);
    return null;
  }
}

async function fileBugReport(captured: CapturedError, preCaptured: Blob | null): Promise<void> {
  // If the screenshot was captured up-front (the "show preview in toast" path),
  // reuse it — re-capturing here would be wasted work and would race against
  // any DOM the user has touched while reading the prompt.
  const screenshot = preCaptured ?? await captureScreenshot();
  const form = new FormData();
  form.append('description', `${captured.errorType}: ${captured.message}`.slice(0, 200));
  form.append('errorType', captured.errorType);
  form.append('stackTrace', captured.stack.slice(0, 8000));
  form.append('auto', '1');
  form.append('url', window.location.href);
  form.append('userAgent', navigator.userAgent);
  form.append('viewport', `${window.innerWidth}×${window.innerHeight}`);
  form.append('timestamp', new Date().toISOString());
  if (screenshot) form.append('screenshot', screenshot, 'screenshot.png');

  const data = await api<{ issueUrl?: string | null; dedup?: boolean }>(
    DASHBOARD_PATHS.bugReport,
    { method: 'POST', body: form },
  );
  if (data.issueUrl) {
    const verb = data.dedup ? 'Reproduced — comment added' : 'Filed';
    toast.success(verb, {
      description: data.issueUrl,
      action: { label: 'Open', onClick: () => window.open(data.issueUrl!, '_blank') },
    });
  } else {
    toast.success('Bug report saved (GitHub not configured)');
  }
}

function offerToFile(captured: CapturedError, preview: Blob | null) {
  const key = `${captured.errorType}|${topFrameKey(captured.stack)}`;
  if (promptedKeys.has(key)) {
    // Already prompted this run — release any blob URL we won't use.
    return;
  }
  promptedKeys.add(key);

  const previewUrl = preview ? URL.createObjectURL(preview) : null;

  // Description: optional preview thumbnail above the prompt text. Sonner
  // renders ReactNode for description, so we hand it a stack of nodes.
  const description = previewUrl ? (
    <div className="flex flex-col gap-2">
      <img
        src={previewUrl}
        alt="Screenshot preview"
        className="max-h-48 w-full object-contain rounded border border-border bg-zinc-950"
      />
      <span>File this as a GitHub issue?</span>
    </div>
  ) : 'File this as a GitHub issue?';

  toast(`${captured.errorType}: ${captured.message}`.slice(0, 120), {
    description,
    duration: 20_000,
    action: {
      label: 'File',
      onClick: () => {
        toast.promise(fileBugReport(captured, preview), {
          loading: previewUrl ? 'Posting…' : 'Capturing screenshot + posting…',
          success: () => 'Posted',
          error: (e) => `Failed: ${e?.message ?? e}`,
        });
      },
    },
    cancel: { label: 'Dismiss', onClick: () => {} },
    onAutoClose: () => { if (previewUrl) URL.revokeObjectURL(previewUrl); },
    onDismiss:   () => { if (previewUrl) URL.revokeObjectURL(previewUrl); },
  });
}

export function ClientErrorReporter() {
  // Stash latest debug-mode flag in a ref so the global handlers (which
  // close over a module-scope value) always read the current setting.
  const debugRef = useRef(getDebugModeEnabled());
  // Admin-controlled toggle: when on, every detected error pre-captures a
  // screenshot before showing the prompt so the user can see what was on
  // screen at the moment the error fired.
  const consoleShotsRef = useRef(false);

  useEffect(() => {
    const refresh = () => { debugRef.current = getDebugModeEnabled(); };
    const onStorage = (e: StorageEvent) => { if (e.key === STORAGE_KEY) refresh(); };
    window.addEventListener('storage', onStorage);
    refresh();
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  useEffect(() => {
    // The toggle is only consulted inside the debug-mode handler, so there's
    // no reason to hit an authenticated endpoint for signed-out visitors —
    // that probe fires app-wide, including on public pages like /accept-invite.
    if (!getDebugModeEnabled()) return;
    let cancelled = false;
    const load = async () => {
      try {
        const data = await api<{ consoleScreenshots?: boolean }>(DASHBOARD_PATHS.settingsErrorReporter);
        if (!cancelled) consoleShotsRef.current = !!data.consoleScreenshots;
      } catch {
        // Toggle is purely UX — silently leave it off if the fetch fails.
      }
    };
    void load();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    async function handle(captured: CapturedError) {
      if (!debugRef.current) return;
      // Capture the screenshot BEFORE we hand off to the toast layer so what
      // the user sees in the preview matches the moment the error fired —
      // any further mouse movement / nav / redraw would otherwise leak in.
      const preview = consoleShotsRef.current ? await captureScreenshot() : null;
      offerToFile(captured, preview);
    }

    const onError = (event: ErrorEvent) => {
      const err = event.error as Error | undefined;
      if (!err) return;
      void handle({
        errorType: err.name || 'Error',
        message: err.message || String(event.message || 'unknown'),
        stack: err.stack || '',
      });
    };
    const onRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      if (!reason) return;
      const err: Error = reason instanceof Error ? reason : new Error(String(reason));
      void handle({
        errorType: err.name || 'UnhandledRejection',
        message: err.message || String(reason),
        stack: err.stack || '',
      });
    };

    // console.error wrapper — only fires the prompt path when debug mode is on
    // AND there's an Error in the args (call sites that pass a bare string
    // log message aren't actionable).
    const origConsoleError = console.error;
    const wrappedConsoleError = (...args: unknown[]) => {
      origConsoleError.apply(console, args as []);
      if (!debugRef.current) return;
      const errArg = args.find((a) => a instanceof Error) as Error | undefined;
      if (!errArg) return;
      void handle({
        errorType: errArg.name || 'ConsoleError',
        message: errArg.message || args.map(String).join(' ').slice(0, 200),
        stack: errArg.stack || '',
      });
    };
    console.error = wrappedConsoleError;

    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);
    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
      // Only restore if nothing else has wrapped console.error in the
      // meantime; otherwise we'd clobber the other wrapper's chain.
      if (console.error === wrappedConsoleError) console.error = origConsoleError;
    };
  }, []);

  return null;
}
